import React, { useEffect, useRef } from 'react';
import { buildInfo } from '@shared/build/buildInfo';
import DateTime from '@shared/common/DateTime/DateTime';
import DateTimeFormatHelper from '@shared/common/DateTimeFormatHelper';
import InovxLoader from '@shared/global/InovxLoader';

interface AboutDialogProps {
    onClose: () => void;
}

const AboutDialog: React.FC<AboutDialogProps> = ({ onClose }) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const buildNumber = buildInfo.buildNumber || InovxLoader.t('common:about.developmentBuild');
    const buildDate = buildInfo.buildDateUtc
        ? formatBuildDate(buildInfo.buildDateUtc)
        : InovxLoader.t('common:about.buildDateUnavailable');

    useEffect(() => {
        closeButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onClose();
            } else if (event.key === 'Tab') {
                event.preventDefault();
                event.stopPropagation();
                closeButtonRef.current?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [onClose]);

    return (
        <section
            className="about-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-dialog-title">
            <button
                ref={closeButtonRef}
                type="button"
                className="about-dialog-close"
                aria-label={InovxLoader.t('common:header.close')}
                onClick={onClose}>
                <span aria-hidden="true">&times;</span>
            </button>
            <h1 id="about-dialog-title">{InovxLoader.t('common:about.productName')}</h1>
            <dl className="about-build-info">
                <div>
                    <dt>{InovxLoader.t('common:about.buildNumber')}</dt>
                    <dd>{buildNumber}</dd>
                </div>
                <div>
                    <dt>{InovxLoader.t('common:about.buildDate')}</dt>
                    <dd>{buildDate}</dd>
                </div>
            </dl>
            <p className="about-copyright">
                {InovxLoader.t('common:about.copyright')}
            </p>
        </section>
    );
};

function formatBuildDate(buildDateUtc: Date): string {
    const isoTimestamp = buildDateUtc.toISOString();
    const formattedDate = DateTimeFormatHelper.getFormatDate(isoTimestamp);
    const formattedTime = DateTimeFormatHelper.getFormatTime(isoTimestamp, true);
    const timeZone = DateTime.getTimezone();
    const timeZoneLabel = getTimeZoneLabel(buildDateUtc, timeZone);

    return `${formattedDate} ${formattedTime} (${timeZoneLabel})`;
}

function getTimeZoneLabel(date: Date, timeZone: string): string {
    try {
        const locale = InovxLoader.getLocaleData().lng || undefined;
        const timeZonePart = new Intl.DateTimeFormat(locale, {
            timeZone,
            timeZoneName: 'short',
        }).formatToParts(date).find(part => part.type === 'timeZoneName');

        return timeZonePart?.value || timeZone;
    } catch {
        return timeZone;
    }
}

export default AboutDialog;
